"use client"

import { useSyncExternalStore } from "react"

import { AUTH_TOKEN_COOKIE } from "@lib/constants"
import { getSessionCookie, setSessionCookie } from "@lib/util/get-session-cookie-client"

export const setSessionAuthToken = (token: string) => {
  setSessionCookie(AUTH_TOKEN_COOKIE, token, 3 * 60 * 60) // 3 hours
}

// This store is a client-side singleton.
// It initializes its state from the cookie on first use.
class AuthTokenStore {
  private listeners = new Set<() => void>()
  private token: string | null = null
  private channel: BroadcastChannel | null = null
  private isInitialized = false
  private cleanupFunction: (() => void) | null = null

  private initialize() {
    if (this.isInitialized || typeof window === "undefined") {
      return
    }
    this.isInitialized = true
    this.token = getSessionCookie(AUTH_TOKEN_COOKIE)

    const handleStorageChange = () => {
      const newToken = getSessionCookie(AUTH_TOKEN_COOKIE)
      if (newToken !== this.token) {
        this.token = newToken
        this.notifyListeners()
      }
    }

    // Listen for our custom cookie change event
    window.addEventListener("cookieChange", handleStorageChange as EventListener)
    // Re-check on focus for changes in other tabs
    window.addEventListener("focus", handleStorageChange)

    // Use BroadcastChannel for robust cross-tab sync
    if (typeof BroadcastChannel !== "undefined") {
      this.channel = new BroadcastChannel("auth-sync")
      this.channel.onmessage = handleStorageChange
    }

    // Clean up on page unload to prevent memory leaks
    const handlePageUnload = () => {
      this.forceCleanup()
    }
    window.addEventListener("beforeunload", handlePageUnload)
    window.addEventListener("pagehide", handlePageUnload)

    this.cleanupFunction = () => {
      window.removeEventListener("cookieChange", handleStorageChange as EventListener)
      window.removeEventListener("focus", handleStorageChange)
      window.removeEventListener("beforeunload", handlePageUnload)
      window.removeEventListener("pagehide", handlePageUnload)
      this.channel?.close()
      this.channel = null
    }
  }

  subscribe = (listener: () => void) => {
    this.initialize()
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)

      if (this.listeners.size === 0) {
        setTimeout(() => {
          if (this.listeners.size === 0) {
            this.cleanup()
          }
        }, 100)
      }
    }
  }

  getSnapshot = () => {
    this.initialize()
    return this.token
  }

  private cleanup() {
    if (!this.isInitialized || typeof window === "undefined") {
      return
    }

    this.cleanupFunction?.()

    this.cleanupFunction = null
    this.isInitialized = false
    this.token = null
  }

  private forceCleanup() {
    if (typeof window === "undefined") {
      return
    }

    this.cleanupFunction?.()

    this.listeners.clear()
    this.cleanupFunction = null
    this.isInitialized = false
    this.token = null
  }

  private notifyListeners() {
    this.listeners.forEach((listener) => listener())
  }
}

const authTokenStore = new AuthTokenStore()

/**
 * A hook to safely access the auth token on both server and client.
 *
 * @param initialToken - The token value from the server render. This is crucial for preventing hydration mismatches.
 * @returns The current auth token.
 */
export const useAuthToken = (initialToken?: string) => {
  // `useSyncExternalStore` is the key to preventing hydration mismatches.
  // 1. `subscribe`: How to know when the value changes on the client.
  // 2. `getSnapshot`: How to get the current value on the client.
  // 3. `getServerSnapshot`: How to get the value for the server render.
  //    This MUST match the value used in the first client render.
  //    By using `initialToken`, we ensure the server and client are in sync.
  const token = useSyncExternalStore(authTokenStore.subscribe, authTokenStore.getSnapshot, () => initialToken || null)

  return token
}
